import {
  applyFormat$,
  applyListType$,
  ButtonWithTooltip,
  currentFormat$,
  currentListType$,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  MDXEditor,
  type MDXEditorMethods,
  MultipleChoiceToggleGroup,
  markdownShortcutPlugin,
  Separator,
  SingleChoiceToggleGroup,
  toolbarPlugin,
  useCellValues,
  usePublisher,
} from "@mdxeditor/editor";
import {
  forwardRef,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "@mdxeditor/editor/style.css";
import * as Popover from "@radix-ui/react-popover";
import { useTranslation } from "react-i18next";
import { geminiRequest } from "senf-shared";
import { getTextLines } from "senf-shared/utils/helpers";
import i18n from "senf-shared/utils/i18n";
import { cleanAndParseAiJson } from "senf-shared/utils/reinsertionHelpers";
import styled from "styled-components";
import { useMeasure } from "../../../hooks/useMeasure";
import { cn } from "../../../util/classNameConstructor";
import Skeleton from "../../atoms/skeleton/Skeleton";
import Button from "../button/Button";
import Dropdown from "../dropdown/Dropdown";
import Icon from "../icon/Icon";
import { Input } from "../inputs/Input";
import InvisibleInput from "../inputs/InvisibleInput";
import InsertLinkPopover from "./InsertLinkPopover";
import { LinkButton } from "./LinkButton";
import Markdown from "./Markdown";
import { preventUnderlinePlugin } from "./preventUnderlinePlugin";

const EditorContainer = styled.div`
  font-size: 16px;
  position: relative;
  padding: 0.5rem;
  margin: -0.5rem;
  .mdxeditor-toolbar {
    position: absolute;
    pointer-events: none;
    opacity: 0;
    top: -42px;
    right: 0px;
    padding: 0 4px;
    background-color: var(--background-default);
    border-radius: var(--rounded-md) var(--rounded-md) 0 0;
    border: 1px solid var(--border-subdued);
    transition: opacity 0.15s ease;
    z-index: 10;
    [data-orientation="horizontal"] {
      display: flex;
      flex-direction: row;
    }
    & > * {
      margin-inline: 0;
      button {
        padding: 2px;
        border-radius: var(--rounded-sm) !important;
        width: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &:hover {
          background-color: var(--surface-hovered);
          color: var(--text-highlight);
        }
        &:active {
          svg {
            transform: scale(0.95);
          }
        }
        &:active,
        &[data-state="on"] {
          background-color: var(--action-primary-default);
          color: var(--text-on-primary);
        }
      }
    }
  }
  &:focus-within {
    [data-placeholder] {
      color: var(--text-placeholder);
    }
  }
  [data-placeholder] {
    color: var(--text-disabled);
  }

  [contenteditable],
  .mdxeditor-root-contenteditable > * {
    /* position: absolute; */
    left: 0;
    padding: 0;
    & > * + * {
      margin-top: 1em;
    }
  }
  [list-item] {
    margin: 0;
  }
`;
const IS_BOLD = 0b1 as const;
const IS_ITALIC = 0b10 as const;

const LIST_TITLE_MAP = {
  bullet: "Bulleted list",
  number: "Numbered list",
  bold: "Bold",
  italic: "Italic",
};
const ICON_NAME_MAP = {
  bold: "bold",
  italic: "italic",
  bullet: "list",
  number: "numbered-list",
};
const toggle = (type) => ({
  value: type,
  title: LIST_TITLE_MAP[type] as string,
  contents: <Icon name={ICON_NAME_MAP[type]} className="w-5" />,
});
const BoldItalicToggles = ({ options = ["bold", "italic"] }) => {
  const [currentFormat] = useCellValues(currentFormat$);
  const applyFormat = usePublisher(applyFormat$);

  const ACTIVE_MAP = {
    bold: (currentFormat & IS_BOLD) !== 0,
    italic: (currentFormat & IS_ITALIC) !== 0,
  };

  return (
    <MultipleChoiceToggleGroup
      items={options.map((type) => {
        const item = toggle(type);
        return {
          ...item,
          active: ACTIVE_MAP[type],
          onChange: () => applyFormat(type === "bold" ? "bold" : "italic"),
        };
      })}
    />
  );
};
const ListsToggle = ({ options = ["bullet", "number"] }) => {
  const { t } = useTranslation();
  const [currentListType] = useCellValues(currentListType$);
  const applyListType = usePublisher(applyListType$);
  return (
    <SingleChoiceToggleGroup
      value={currentListType || ""}
      items={options.map((type) => {
        const item = toggle(type);
        return {
          ...item,
          title: t(item.title),
        };
      })}
      onChange={applyListType}
    />
  );
};

const CustomButton = ({ onClick }) => {
  return (
    // <button
    //   onClick={onClick}
    //   type="button"
    //   className="group/magic p-1 hover:bg-surface-hovered rounded-sm -mr-1"
    // >
    //   <Icon
    //     name="ai"
    //     className="w-5 group-hover/magic:text-text-highlight"
    //   />
    // </button>
    <ButtonWithTooltip
      title="Text enhance"
      onClick={onClick}
      className="hover:bg-surface-hovered w-6 h-6 flex items-center justify-center cursor-pointer rounded-sm"
    >
      <Icon
        name="colorful/ai"
        className="w-5 group-hover/magic:text-text-highlight"
      />
    </ButtonWithTooltip>
  );
};

const CustomToolbar = ({ magicButtonClick }) => {
  return (
    <>
      <CustomButton onClick={magicButtonClick} />
      <Separator />
      <BoldItalicToggles options={["bold", "italic"]} />
      <Separator />
      <ListsToggle />
      <Separator />
      <LinkButton />
    </>
  );
};

interface RichTextEditorProps {
  value: string;
  isResetting?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
  propSetIsFocused?: (isFocused: boolean) => void;
  size?: string;
  textAlign?: string;
  className?: string;
  contentEditableClassName?: string;
}

const TextEnhancement = ({
  value,
  result,
  setResult,
  setLoading,
  isCancelled,
  onChange,
  mdxEditorRef,
  loading,
  width,
}) => {
  const [prompt, setPrompt] = useState("");
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when result is available
  useEffect(() => {
    if (result && !loading) {
      // Small delay to ensure component is fully rendered
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [result, loading]);

  // Refocus on mousedown inside this component
  const handleMouseDown = () => {
    // Make sure we focus after any click in the component
    setTimeout(() => {
      if (inputRef.current && result) {
        // inputRef.current.focus();
      }
    }, 10);
  };

  const handleRefine = async () => {
    if (!prompt) return;

    setLoading(true);
    await geminiRequest({
      body: `Give me a new version of the text, based on the following instructions: ${prompt}.
    
    Initial text: ${value}

    Second version of the text: ${result}
    `,
      assistantId: "default",
      lang: i18n.resolvedLanguage,
    }).then((res) => {
      console.log(prompt, res);
      if (!isCancelled.current) {
        const result = cleanAndParseAiJson(res || "");

        if (result === "") {
          setResult(null);
        } else {
          setResult(result);
        }
        setLoading(false);
        setPrompt("");

        // Ensure input gets focused again after loading
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 50);
      }
    });
  };

  const handleAccept = () => {
    if (result) {
      onChange(result);
      mdxEditorRef?.current?.setMarkdown(result);

      setTimeout(() => {
        setResult(null);
        setLoading(false);
        setPrompt("");
      }, 500);
    }
  };

  const handleInterrupt = () => {
    isCancelled.current = true;
    setLoading(false);
    setResult(null);
    setPrompt("");
  };

  const resultLines = useMemo(
    () =>
      Math.max(
        2,
        Math.min(
          5,
          getTextLines(result ?? value, {
            font: "16px Nunito",
            width: width,
            lineHeight: "22px",
          }),
        ),
      ),
    [result, value, width],
  );

  console.log(resultLines);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: <explanation>
    // biome-ignore lint/nursery/useUniqueElementIds: <explanation>
    <div
      id="textEnhanceBox"
      className="h-auto min-h-10 rounded-2xl p-3 mt-2 bg-surface-default flex flex-col gap-5 justify-between shadow-xl border border-border-default w-[calc(var(--radix-popover-trigger-width)+1.5rem)] max-h-[calc(var(--radix-popover-content-available-height)-1.5rem)] overflow-y-auto leading-normal"
      onMouseDown={handleMouseDown}
    >
      {loading && (
        <div className="flex items-center justify-between w-full h-full gap-5">
          <Skeleton count={resultLines} ragged className="w-full " />
          {!result && (
            <Button size="md" leadingIcon="x-md" onClick={handleInterrupt} />
          )}
        </div>
      )}
      {result && !loading && (
        <Markdown className="hyphens-auto break-words">{result}</Markdown>
      )}
      {result && (
        <div className="flex flex-col gap-3 shrink-0 items-end">
          <Input
            ref={inputRef}
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleRefine();
              }
            }}
            placeholder={t("global:refineResult")}
            onClick={(e) => {
              e.stopPropagation();
            }}
            autoFocus
            // leadingIcon={"connection"}
            leadingIcon="colorful/ai"
            trailingIcon={prompt ? (loading ? "loading" : "enter") : undefined}
            disabled={loading}
            trailingIconProps={{
              onClick: loading ? undefined : handleRefine,
              label: loading ? undefined : t("global:refineResult"),
              className: cn(
                loading ? undefined : "hover:bg-surface-hovered",
                "flex items-center aspect-square",
              ),
            }}
            // append={
            //   prompt ? (
            //     <Button
            //       variant="shapeless"
            //       size="sm"
            //       leadingIcon="colorful/ai"
            //       classNameWrapper="-m-1 self-center -translate-y-px"
            //       onClick={handleRefine}
            //     />
            //   ) : null
            // }
          />
          <div className="flex justify-end gap-2 shrink-0">
            <Button
              size="md"
              onClick={() => {
                setResult(null);
                setLoading(false);
                setPrompt("");
              }}
              text={t("global:cancel")}
            />
            <Button
              variant="primary"
              size="md"
              text={t("global:accept")}
              onClick={handleAccept}
              disabled={!!prompt}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Editor = ({
  value,
  isResetting,
  placeholder,
  onChange,
  mdxEditorRef,
  size,
  textAlign,
  magicButtonClick,
  contentEditableClassName,
  ...props
}) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Tab") {
      event.preventDefault();

      // Shift+Tab goes backward, Tab goes forward
      const focusableElements = Array.from(
        document.querySelectorAll<HTMLElement>(
          'a, button, input, textarea, select, [contenteditable], [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"),
      );

      const index = focusableElements.indexOf(
        document.activeElement as HTMLElement,
      );
      const next = event.shiftKey ? index - 1 : index + 1;
      const nextElement = focusableElements[next];
      console.log(index, next);

      if (nextElement) {
        nextElement.focus();
      }
    }
  };

  return (
    <div className="relative [&:hover:focus-within_.mdxeditor-toolbar]:pointer-events-auto [&:hover:focus-within_.mdxeditor-toolbar]:opacity-100">
      <EditorContainer onKeyDown={handleKeyDown}>
        <MDXEditor
          ref={mdxEditorRef}
          key={isResetting ? "reset" : undefined}
          markdown={value || ""}
          placeholder={placeholder}
          className={cn(
            "markdown text-text-default relative whitespace-pre-wrap",
            // biome-ignore lint/nursery/noSecrets: this is not a secret
            "[&_[contenteditable]+div]:whitespace-normal",
            props?.textAlign === "center" && "centeredMarkdown",
            "font-sans",
            size,
          )}
          contentEditableClassName={contentEditableClassName}
          onChange={onChange}
          plugins={[
            linkPlugin(),
            linkDialogPlugin({
              LinkDialog: () => <InsertLinkPopover />,
            }),
            listsPlugin(),
            markdownShortcutPlugin(),
            preventUnderlinePlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <CustomToolbar magicButtonClick={magicButtonClick} />
              ),
            }),
          ]}
        />
      </EditorContainer>
    </div>
  );
};

const RichTextEditor = forwardRef<MDXEditorMethods, RichTextEditorProps>(
  (
    {
      value,
      isResetting,
      placeholder,
      onChange,
      propSetIsFocused = () => {},
      size,
      className,
      contentEditableClassName,
      ...props
    },
    ref,
  ) => {
    const fallbackRef = useRef<MDXEditorMethods>(null);
    const mdxEditorRef = ref || fallbackRef;
    const [{ ref: containerRef }, { width: containerWidth }] =
      useMeasure<HTMLInputElement>();
    const isCancelled = useRef(false);
    const [loading, setLoading] = useState(false);
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const [result, setResult] = useState<string | Record<string, any> | null>(
      null,
    );
    const [isScrolling, setIsScrolling] = useState(false);

    useEffect(() => {
      const abortController = new AbortController();
      document.addEventListener("wheel", () => setIsScrolling(true), {
        capture: true,
        signal: abortController.signal,
      });
      document.addEventListener("mousemove", () => setIsScrolling(false), {
        capture: true,
        signal: abortController.signal,
      });
      return () => {
        abortController.abort();
      };
    }, []);

    useEffect(() => {
      const editor = (mdxEditorRef as unknown as RefObject<MDXEditorMethods>)
        ?.current;
      if (editor) {
        editor.setMarkdown(value || "");
      }
    }, [value, mdxEditorRef]);

    const magicButtonClick = async () => {
      isCancelled.current = false;
      setResult(null);
      setLoading(true);

      setTimeout(() => {
        document.getElementById("textEnhanceBox")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }, 100);

      await geminiRequest({
        body: `
      
      TEXT: ${value}
      
      `,
        assistantId: "textEnhancer",
        lang: i18n.resolvedLanguage,
      }).then((res) => {
        if (!isCancelled.current) {
          const result = cleanAndParseAiJson(res || "");
          setResult(result);
          setLoading(false);
        }
      });
    };

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: <explanation>
      <div
        className={cn("w-full", className)}
        onFocus={() => {
          propSetIsFocused(true);
        }}
        onBlur={() => {
          propSetIsFocused(false);
        }}
        ref={containerRef}
      >
        <Editor
          value={value}
          onChange={onChange}
          mdxEditorRef={mdxEditorRef}
          size={size}
          textAlign={props.textAlign}
          magicButtonClick={magicButtonClick}
          isResetting={isResetting}
          placeholder={placeholder}
          contentEditableClassName={contentEditableClassName}
        />
        <Popover.Root open={loading || !!result}>
          <Popover.Anchor />
          <Popover.Portal
            container={
              document.querySelector("main") || document.getElementById("root")
            }
          >
            {/* <div className={cn(isScrolling && "pointer-events-none")}> */}
            <Popover.Content side="bottom" avoidCollisions={false}>
              <TextEnhancement
                value={value}
                result={result}
                setResult={setResult}
                setLoading={setLoading}
                isCancelled={isCancelled}
                onChange={onChange}
                mdxEditorRef={mdxEditorRef}
                loading={loading}
                width={containerWidth}
              />
            </Popover.Content>
            {/* </div> */}
          </Popover.Portal>
        </Popover.Root>
      </div>
    );
  },
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
